import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence, useInView } from 'framer-motion';
import { MapPin, Phone, Instagram, Facebook, ArrowRight, Star, Ticket, Utensils, Quote, ArrowDown, Heart, Menu as MenuIcon, X, ExternalLink, Clock, Users, Award, ChefHat, Flame, Play, Calendar, Mail, Send, Check, Sparkles, Trophy, Target, Zap, Camera, MessageCircle, BadgeCheck, ChevronRight, ChevronLeft, Minus, Wine, AlignLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/* --- THEME CONFIGURATION --- */
const THEME = {
  blue: "#0087E6",
  navy: "#0F172A",
  sky: "#F0F9FF",
  white: "#FFFFFF",
  gold: "#FFD700",
};

/* --- DATA: THE MODRIC PIZZA --- */
const PIZZA = {
  id: 10,
  name: "The Modrić",
  nickname: "The Maestro",
  price: "18",
  description: "A masterclass in balance. Sharp, salty Paški Sir meets the subtle sweetness of fig jam.",
  story: "In football, Luka Modrić defines elegance. He sees angles others miss. He controls the tempo. This pizza is built on that same philosophy. It doesn't shout; it orchestrates. The intense salinity of the Pag sheep cheese is the defense, perfectly assisting the sweetness of the figs. It is ageless, sophisticated, and undeniably Croatian.",
  ingredients: [
    { name: "Paški Sir Cream", role: " The Captain", desc: "Aged 12 months. Sharp, crystalline, distinct." },
    { name: "Prosciutto Crudo", role: "The Winger", desc: "Dalmatian ham, dried in the Bura wind." },
    { name: "Fig Jam", role: "The Playmaker", desc: "Provides the crucial assist of sweetness." },
    { name: "Walnuts", role: "The Enforcer", desc: "Toasted crunch for texture and depth." }
  ],
  stats: [
    { label: "Complexity", value: 95 },
    { label: "Elegance", value: 99 },
    { label: "Intensity", value: 85 },
  ],
  pairing: "Pošip White Wine (Korčula)",
  heroImage: "https://images.unsplash.com/photo-1513104890138-7c749659a591?q=80&w=2000&auto=format&fit=crop",
  detailImage: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?q=80&w=1000&auto=format&fit=crop"
};

/* --- HERO SECTION --- */
const ProductHero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0.3, 0.8]);

  return (
    <header ref={containerRef} className="relative w-full h-[90vh] min-h-[600px] bg-[#0F172A] text-white overflow-hidden flex items-end">
       
       {/* Cinematic Background */}
       <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
          <img src={PIZZA.heroImage} alt={PIZZA.name} className="w-full h-full object-cover" />
       </motion.div>
       
       <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1] bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent" />
       
       {/* Content */}
       <div className="container mx-auto px-4 sm:px-6 relative z-10 pb-16 sm:pb-24">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
             <div className="flex items-center gap-3 mb-6">
                <span className="bg-[#0087E6] text-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full">
                   Player Profile
                </span>
                <span className="text-[#FFD700] font-anton text-xl">#{PIZZA.id}</span>
             </div>

             <h1 className="font-anton text-6xl sm:text-7xl md:text-9xl text-white uppercase leading-[0.8] mb-6">
                {PIZZA.name}
             </h1>
             
             <p className="font-serif italic text-white/80 text-xl sm:text-2xl max-w-2xl mb-10">
                "{PIZZA.nickname}"
             </p>

             <div className="flex flex-wrap items-center gap-6 sm:gap-10 border-t border-white/20 pt-8">
                <div>
                   <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Transfer Value</div>
                   <div className="font-anton text-3xl">€{PIZZA.price}</div>
                </div>
                <div>
                   <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Style</div>
                   <div className="font-anton text-3xl">Bianca</div>
                </div>
                <div>
                   <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Origin</div>
                   <div className="font-anton text-3xl">Pag/Napoli</div>
                </div>
             </div>
          </motion.div>
       </div>
    </header>
  );
};

/* --- THE STORY (EDITORIAL) --- */
const TheStory = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="the-story" className="py-20 sm:py-32 bg-white" ref={ref}>
       <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-24">
             
             {/* Left Column: The Narrative */}
             <motion.div 
               className="lg:col-span-7"
               initial={{ opacity: 0, y: 20 }}
               animate={isInView ? { opacity: 1, y: 0 } : {}}
               transition={{ duration: 0.8 }}
             >
                <span className="text-[#0087E6] font-manrope font-bold text-[10px] sm:text-xs uppercase tracking-[0.2em] mb-4 block">
                   Match Analysis
                </span>
                <h2 className="font-anton text-4xl sm:text-5xl md:text-6xl text-[#0F172A] uppercase mb-8 leading-[0.95]">
                   Vision. Control. <br/> <span className="text-[#0087E6]">Pure Class.</span>
                </h2>
                
                <div className="prose prose-lg text-gray-600 font-manrope leading-relaxed space-y-6">
                   <p className="first-letter:text-5xl first-letter:font-anton first-letter:text-[#0F172A] first-letter:mr-3 first-letter:float-left">
                      {PIZZA.story}
                   </p>
                   <p>
                      We didn't want to create just another "Quattro Formaggi." We wanted a pizza that told a story of the terrain. The sheep on Pag graze on wild sage dusted with sea salt brought by the Bura wind. That flavor is intense, sharp, and unmistakably Croatian.
                   </p>
                   <p>
                      To balance that power, we needed a playmaker. The fig jam steps into the pocket of space, providing a sweet release that cuts through the richness. The walnuts add the crunch, the tackle, the physical presence.
                   </p>
                </div>

                <div className="mt-12 p-8 bg-[#F0F9FF] rounded-2xl border border-blue-50 relative">
                   <Quote className="absolute top-6 right-6 text-[#0087E6]/20" size={40} />
                   <p className="font-serif italic text-xl text-[#0F172A] relative z-10">
                      "It plays the game at a different pace. You don't rush this pizza. You savor the movement of flavors."
                   </p>
                   <div className="mt-4 font-bold text-xs uppercase tracking-widest text-[#0087E6]">— Head Pizzaiolo</div>
                </div>
             </motion.div>

             {/* Right Column: Visuals & Stats */}
             <div className="lg:col-span-5 space-y-12">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="relative aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl"
                >
                   <img src={PIZZA.detailImage} alt="Texture" className="w-full h-full object-cover" />
                   <div className="absolute inset-0 bg-[#0087E6]/10 mix-blend-multiply" />
                </motion.div>

                {/* Flavor Profile Bars */}
                <div className="bg-[#0F172A] text-white p-8 rounded-3xl shadow-xl">
                   <h3 className="font-anton text-2xl uppercase mb-6">Player Stats</h3>
                   <div className="space-y-6">
                      {PIZZA.stats.map((stat, i) => (
                         <div key={i}>
                            <div className="flex justify-between mb-2 text-xs font-bold uppercase tracking-widest">
                               <span>{stat.label}</span>
                               <span className="text-[#0087E6]">{stat.value}/100</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                               <motion.div 
                                 initial={{ width: 0 }}
                                 animate={isInView ? { width: `${stat.value}%` } : {}}
                                 transition={{ duration: 1, delay: 0.5 + (i * 0.2) }}
                                 className="h-full bg-[#0087E6]"
                               />
                            </div>
                         </div>
                      ))}
                   </div>
                </div>
             </div>

          </div>
       </div>
    </section>
  );
};

/* --- THE FORMATION (INGREDIENTS) --- */
const TheFormation = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="formation" className="py-20 bg-[#F0F9FF] border-t border-white" ref={ref}>
       <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
             <span className="text-[#0087E6] font-manrope font-bold text-[10px] sm:text-xs uppercase tracking-[0.2em] mb-3 block">Squad List</span>
             <h2 className="font-anton text-4xl sm:text-6xl text-[#0F172A] uppercase">The Starting IV</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
             {PIZZA.ingredients.map((ing, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-sm border border-blue-50 group hover:-translate-y-2 transition-transform duration-300"
                >
                   <div className="w-10 h-10 rounded-full bg-[#0F172A] text-white flex items-center justify-center font-anton mb-4 text-sm">
                      {i + 1}
                   </div>
                   <div className="text-[10px] font-bold uppercase tracking-widest text-[#0087E6] mb-2">{ing.role}</div>
                   <h3 className="font-anton text-2xl text-[#0F172A] uppercase mb-3">{ing.name}</h3>
                   <p className="font-manrope text-sm text-gray-500 leading-relaxed">{ing.desc}</p>
                </motion.div>
             ))}
          </div>
       </div>
    </section>
  );
};

/* --- PAIRING & CTA --- */
const Pairing = () => {
   return (
      <section id="pairing" className="py-20 sm:py-32 bg-white">
         <div className="container mx-auto px-4 sm:px-6">
            <div className="bg-[#0F172A] rounded-3xl p-8 sm:p-16 text-center relative overflow-hidden">
               {/* Decorative Background */}
               <div className="absolute top-0 right-0 w-64 h-64 bg-[#0087E6]/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
               <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#0087E6]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
               
               <div className="relative z-10 max-w-2xl mx-auto">
                  <div className="inline-flex items-center gap-2 text-[#FFD700] mb-6">
                     <Wine size={20} />
                     <span className="font-bold uppercase tracking-widest text-xs">Perfect Match</span>
                  </div>
                  
                  <h2 className="font-anton text-3xl sm:text-5xl text-white uppercase mb-6">
                     Pair with: {PIZZA.pairing}
                  </h2>
                  <p className="font-serif italic text-white/60 text-lg mb-10">
                     "The crisp acidity of the Pošip grape cuts through the richness of the cheese, completing the experience."
                  </p>
                  
                  <button className="bg-white text-[#0F172A] px-8 py-4 rounded-full font-bold uppercase tracking-widest text-sm hover:bg-[#0087E6] hover:text-white transition-colors shadow-xl">
                     Book This Table
                  </button>
               </div>
            </div>
         </div>
      </section>
   );
};


/* --- MAIN APP (PIZZA DETAIL PAGE) --- */
export default function App() {
  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden selection:bg-[#0087E6] selection:text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400;1,700&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #F0F9FF; }
        ::-webkit-scrollbar-thumb { background: #0087E6; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #0F172A; }
      `}</style>
      
      <Navbar />
      <ProductHero />
      <TheStory />
      <TheFormation />
      <Pairing />
      <Footer />
    </div>
  );
}